import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import api from '../utils/api';

export default function PaymentForm({ show, onHide, orderId, installmentId, installmentNumber, amount, totalInstallments, onSuccess }) {
  const [formData, setFormData] = useState({
    amount: amount || '',
    note: ''
  });
  const [slipFile, setSlipFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setFormData({
        amount: amount || '',
        note: ''
      });
      setSlipFile(null);
      setPreview(null);
      setError('');
      setSuccess(false);
      setIsSubmitting(false);
    }
  }, [show, amount]);

  // ข้อมูลบัญชีธนาคาร
  const bankAccount = {
    name: 'นายกรณัฏฐ์ นาราภัทรพิมพ์กุล',
    bank: 'SCB',
    accountNumber: '428-071561-5'
  };

  const formatCurrency = (value) => {
    if (value == null || value === '') {
      return '-';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
      return '-';
    }
    return `฿${number.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleCopyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(bankAccount.accountNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = bankAccount.accountNumber;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        setError('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (jpg, jpeg, png)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('ไฟล์ขนาดใหญ่เกินไป (สูงสุด 5MB)');
        return;
      }

      setSlipFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!orderId) {
      setError('ไม่พบหมายเลขคำสั่งซื้อ');
      console.error('PaymentForm: orderId is missing');
      return;
    }

    if (!slipFile) {
      setError('กรุณาอัปโหลดสลิปโอนเงิน');
      return;
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      setError('กรุณากรอกจำนวนเงินที่ถูกต้อง');
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('order_id', orderId);
      if (installmentId) {
        formDataToSend.append('installment_id', installmentId);
      }
      formDataToSend.append('amount', formData.amount);
      if (formData.note) {
        formDataToSend.append('note', formData.note);
      }
      formDataToSend.append('slip', slipFile);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('กรุณาเข้าสู่ระบบก่อน');
        setIsSubmitting(false);
        return;
      }

      console.log('Submitting payment:', {
        orderId,
        installmentId,
        amount: formData.amount,
        hasFile: !!slipFile,
        fileName: slipFile?.name
      });

      const response = await api.post('/api/payments', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Payment response:', response.data);

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess && onSuccess();
          handleClose();
        }, 2000);
      } else {
        setError(response.data.error || 'ไม่สามารถแจ้งชำระเงินได้');
      }
    } catch (err) {
      console.error('Submit payment error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error || err.message || 'ไม่สามารถแจ้งชำระเงินได้ กรุณาลองใหม่อีกครั้ง';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ amount: amount || '', note: '' });
    setSlipFile(null);
    setPreview(null);
    setError('');
    setSuccess(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-credit-card me-2"></i>
          แจ้งชำระเงิน
          {installmentNumber && ` (งวดที่ ${installmentNumber})`}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {success && (
            <Alert variant="success" className="mb-3">
              <i className="bi bi-check-circle me-2"></i>
              แจ้งชำระเงินสำเร็จ รอการอนุมัติจากแอดมิน
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="bi bi-exclamation-circle me-2"></i>
              {error}
            </Alert>
          )}

          {/* ข้อมูลบัญชีธนาคาร */}
          <Alert variant="info" className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong className="d-block mb-1">
                  <i className="bi bi-bank me-2"></i>
                  ข้อมูลบัญชีธนาคาร
                </strong>
                <div className="small">
                  <div>ชื่อบัญชี: {bankAccount.name}</div>
                  <div>ธนาคาร: {bankAccount.bank} (ไทยพาณิชย์)</div>
                  <div className="mt-2">
                    <strong>เลขบัญชี: {bankAccount.accountNumber}</strong>
                  </div>
                </div>
              </div>
              <Button
                variant={copied ? 'success' : 'outline-primary'}
                size="sm"
                onClick={handleCopyAccountNumber}
                className="ms-3"
              >
                {copied ? (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    คัดลอกแล้ว
                  </>
                ) : (
                  <>
                    <i className="bi bi-clipboard me-1"></i>
                    คัดลอกเลขบัญชี
                  </>
                )}
              </Button>
            </div>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>คำสั่งซื้อ #</Form.Label>
            <Form.Control type="text" value={orderId} disabled />
          </Form.Group>

          {installmentNumber ? (
            <Alert variant="warning" className="mb-3 border-0 shadow-sm" style={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
              <div className="d-flex align-items-start">
                <div className="me-3 mt-1">
                  <i className="bi bi-calendar-check fs-4 text-warning"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-baseline mb-2">
                    <h6 className="mb-0 me-2" style={{ color: '#856404', fontWeight: '600' }}>
                      ชำระงวดที่ {installmentNumber}
                    </h6>
                    {totalInstallments && (
                      <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                        จากทั้งหมด {totalInstallments} งวด
                      </small>
                    )}
                  </div>
                  {amount && (
                    <div className="d-flex align-items-center" style={{ 
                      backgroundColor: '#fff', 
                      padding: '8px 12px', 
                      borderRadius: '6px',
                      border: '1px solid #ffc107'
                    }}>
                      <i className="bi bi-cash-coin me-2 text-success"></i>
                      <span className="text-muted me-2" style={{ fontSize: '0.9rem' }}>ยอดชำระ:</span>
                      <strong className="text-success" style={{ fontSize: '1.1rem' }}>
                        {formatCurrency(amount)}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
            </Alert>
          ) : amount ? (
            <Alert variant="info" className="mb-3 border-0 shadow-sm" style={{ backgroundColor: '#d1ecf1', borderLeft: '4px solid #0dcaf0' }}>
              <div className="d-flex align-items-start">
                <div className="me-3 mt-1">
                  <i className="bi bi-wallet2 fs-4 text-info"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-2" style={{ color: '#055160', fontWeight: '600' }}>
                    ชำระเต็มจำนวน
                  </h6>
                  <div className="d-flex align-items-center" style={{ 
                    backgroundColor: '#fff', 
                    padding: '8px 12px', 
                    borderRadius: '6px',
                    border: '1px solid #0dcaf0'
                  }}>
                    <i className="bi bi-cash-coin me-2 text-success"></i>
                    <span className="text-muted me-2" style={{ fontSize: '0.9rem' }}>ยอดชำระ:</span>
                    <strong className="text-success" style={{ fontSize: '1.1rem' }}>
                      {formatCurrency(amount)}
                    </strong>
                  </div>
                </div>
              </div>
            </Alert>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>
              จำนวนเงินที่โอน <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              required
            />
            <Form.Text className="text-muted">
              {installmentNumber && amount ? (
                <>กรุณากรอกจำนวนเงินที่โอนจริงตามสลิป (ยอดที่คาดหวัง: {formatCurrency(amount)})</>
              ) : (
                <>กรุณากรอกจำนวนเงินที่โอนจริงตามสลิป</>
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              อัปโหลดสลิปโอนเงิน <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange}
              required
            />
            <Form.Text className="text-muted">
              รองรับไฟล์ JPG, JPEG, PNG ขนาดไม่เกิน 5MB
            </Form.Text>
          </Form.Group>

          {preview && (
            <div className="mb-3">
              <Form.Label>ตัวอย่างสลิป:</Form.Label>
              <div className="border rounded p-2 d-flex justify-content-center" style={{ 
                maxHeight: '3000px', 
                overflow: 'auto',
                backgroundColor: '#f8f9fa'
              }}>
                <img
                  src={preview}
                  alt="Preview"
                  style={{ 
                    maxWidth: '250px', 
                    maxHeight: '250px',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>หมายเหตุ (ถ้ามี)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              placeholder="เช่น โอนผ่านแอปธนาคาร, โอนผ่าน PromptPay"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting || success}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                กำลังส่ง...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                แจ้งชำระเงิน
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

